import { useEffect, useMemo, useState, useCallback } from 'react';
import { FiBarChart2, FiEdit2, FiEye } from 'react-icons/fi';
import {
  fetchAgents,
  createAgent,
  updateAgent,
  getAgentCapacity,
  getAgent,
} from '../api/agentService';

const AVAILABILITY_OPTIONS = ['available', 'busy', 'offline'];

const AVAIL_LABEL = {
  available: 'Available',
  busy: 'Busy',
  offline: 'Offline',
};

function AgentModal({ agent, onClose, onSaved }) {
  const isEdit = !!agent?.id;
  const [form, setForm] = useState({
    name: agent?.name || '',
    email: agent?.email || '',
    phone: agent?.phone || '',
    region: agent?.region || '',
    max_capacity: agent?.max_capacity ?? 50,
    availability: agent?.availability || 'available',
    is_active: agent?.is_active ?? true,
    is_locked: agent?.is_locked ?? false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
    setServerError('');
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Name is required.';
    if (!form.email.trim()) next.email = 'Email is required.';
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = 'Enter a valid email.';
    if (!form.max_capacity || Number(form.max_capacity) < 1) next.max_capacity = 'Capacity should be at least 1.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        max_capacity: Number(form.max_capacity),
      };
      if (isEdit) {
        await updateAgent(agent.id, payload);
      } else {
        await createAgent(payload);
      }
      onSaved();
    } catch (err) {
      setServerError(err?.response?.data?.message || 'Operation failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'Edit Agent' : 'Create Agent'}</h3>
          <button className="modal-close" type="button" onClick={onClose} aria-label="Close">x</button>
        </div>
        <form className="stack-form" onSubmit={onSubmit} noValidate>
          <label>
            <span>Name <span className="required-mark">*</span></span>
            <input name="name" value={form.name} onChange={onChange} />
            {errors.name && <span className="error-text">{errors.name}</span>}
          </label>
          <label>
            <span>Email <span className="required-mark">*</span></span>
            <input name="email" type="email" value={form.email} onChange={onChange} />
            {errors.email && <span className="error-text">{errors.email}</span>}
          </label>
          <label>
            <span>Phone</span>
            <input name="phone" type="tel" value={form.phone} onChange={onChange} />
          </label>
          <div className="form-row">
            <label>
              <span>Region</span>
              <input name="region" value={form.region} onChange={onChange} placeholder="Global" />
            </label>
            <label>
              <span>Max Capacity</span>
              <input name="max_capacity" type="number" min="1" value={form.max_capacity} onChange={onChange} />
              {errors.max_capacity && <span className="error-text">{errors.max_capacity}</span>}
            </label>
          </div>
          <div className="form-row">
            <label>
              <span>Availability</span>
              <select name="availability" value={form.availability} onChange={onChange}>
                {AVAILABILITY_OPTIONS.map((o) => <option key={o} value={o}>{AVAIL_LABEL[o]}</option>)}
              </select>
            </label>
            <div className="toggle-field">
              <span className="toggle-field-label">Active</span>
              <label className="toggle-switch">
                <input name="is_active" type="checkbox" checked={form.is_active} onChange={onChange} />
                <span className="toggle-track"><span className="toggle-thumb" /></span>
              </label>
            </div>
            <div className="toggle-field">
              <span className="toggle-field-label">Locked</span>
              <label className="toggle-switch">
                <input name="is_locked" type="checkbox" checked={form.is_locked} onChange={onChange} />
                <span className="toggle-track"><span className="toggle-thumb" /></span>
              </label>
            </div>
          </div>
          {serverError && <p className="error-text">{serverError}</p>}
          <div className="form-actions">
            <button className="primary-btn" type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Agent'}
            </button>
            <button className="ghost-btn" type="button" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CapacityModal({ agent, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getAgentCapacity(agent.id)
      .then((res) => setData(res.data))
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load capacity.'))
      .finally(() => setLoading(false));
  }, [agent.id]);

  const width = data?.max_capacity ? Math.min(100, (data.active_lead_count / data.max_capacity) * 100) : 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card modal-card--sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Capacity - {agent.name}</h3>
          <button className="modal-close" type="button" onClick={onClose} aria-label="Close">x</button>
        </div>
        {loading ? <p>Loading...</p> : null}
        {error ? <p className="error-text">{error}</p> : null}
        {data && (
          <div className="capacity-detail">
            <div className="capacity-row">
              <span>Max Capacity</span><strong>{data.max_capacity}</strong>
            </div>
            <div className="capacity-row">
              <span>Active Leads</span><strong>{data.active_lead_count}</strong>
            </div>
            <div className="capacity-row">
              <span>Remaining</span>
              <strong style={{ color: data.is_over_capacity ? '#f43f5e' : '#10b981' }}>
                {data.remaining_capacity}
              </strong>
            </div>
            {data.is_over_capacity ? <p className="error-text">This agent is over capacity.</p> : null}
            <div className="capacity-bar-wrap">
              <div
                className="capacity-bar"
                style={{
                  width: `${width}%`,
                  background: data.is_over_capacity ? '#f43f5e' : '#6366f1',
                }}
              />
            </div>
          </div>
        )}
        <div className="form-actions">
          <button className="ghost-btn" type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function AgentDrawer({ agentId, onClose, onEdit, onCapacity }) {
  const [agent, setAgent] = useState(null);
  const [capacity, setCapacity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        const [agentRes, capRes] = await Promise.all([
          getAgent(agentId),
          getAgentCapacity(agentId),
        ]);
        if (!mounted) return;
        setAgent(agentRes.data || agentRes);
        setCapacity(capRes.data || capRes);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [agentId]);

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="lead-drawer">
        <div className="drawer-header">
          <h3>{loading ? 'Loading...' : (agent?.name || 'Agent')}</h3>
          <button className="modal-close" type="button" onClick={onClose} aria-label="Close">x</button>
        </div>

        {loading ? <p className="drawer-loading">Loading...</p> : null}

        {!loading && agent ? (
          <>
            <div className="drawer-section">
              <p className="drawer-section-title">Profile</p>
              <div className="drawer-row"><span className="drawer-label">Email</span><span className="drawer-value">{agent.email || '—'}</span></div>
              <div className="drawer-row"><span className="drawer-label">Phone</span><span className="drawer-value">{agent.phone || '—'}</span></div>
              <div className="drawer-row"><span className="drawer-label">Region</span><span className="drawer-value">{agent.region || '—'}</span></div>
              <div className="drawer-row"><span className="drawer-label">Availability</span><span className="drawer-value">{AVAIL_LABEL[agent.availability] || '—'}</span></div>
              <div className="drawer-row"><span className="drawer-label">Status</span><span className="drawer-value">{agent.is_active ? 'Active' : 'Inactive'} / {agent.is_locked ? 'Locked' : 'Unlocked'}</span></div>
            </div>

            <div className="drawer-section">
              <p className="drawer-section-title">Capacity Snapshot</p>
              <div className="drawer-row"><span className="drawer-label">Max</span><span className="drawer-value">{capacity?.max_capacity ?? agent.max_capacity ?? '—'}</span></div>
              <div className="drawer-row"><span className="drawer-label">Active Leads</span><span className="drawer-value">{capacity?.active_lead_count ?? agent.active_lead_count ?? '—'}</span></div>
              <div className="drawer-row"><span className="drawer-label">Remaining</span><span className="drawer-value">{capacity?.remaining_capacity ?? '—'}</span></div>
            </div>

            <div className="drawer-actions">
              <button className="primary-btn" type="button" onClick={() => onEdit(agent)}>
                Edit Agent
              </button>
              <button className="ghost-btn" type="button" onClick={() => onCapacity(agent)}>
                View Capacity
              </button>
            </div>
          </>
        ) : null}
      </aside>
    </>
  );
}

function AgentsPage() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [filterAvail, setFilterAvail] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [filterActive, setFilterActive] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editAgent, setEditAgent] = useState(null);
  const [capacityAgent, setCapacityAgent] = useState(null);
  const [drawerAgentId, setDrawerAgentId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filterAvail) params.availability = filterAvail;
      if (filterRegion) params.region = filterRegion;
      if (filterActive) params.is_active = filterActive === 'true';
      const res = await fetchAgents(params);
      setAgents(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to load agents.');
    } finally {
      setLoading(false);
    }
  }, [filterAvail, filterRegion, filterActive]);

  useEffect(() => { load(); }, [load]);

  const filteredAgents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter((agent) => (
      (agent.name || '').toLowerCase().includes(q)
      || (agent.email || '').toLowerCase().includes(q)
      || (agent.phone || '').toLowerCase().includes(q)
      || (agent.region || '').toLowerCase().includes(q)
    ));
  }, [agents, search]);

  const regions = useMemo(() => {
    const set = new Set();
    agents.forEach((a) => { if (a.region) set.add(a.region); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [agents]);

  const kpis = useMemo(() => {
    const total = agents.length;
    const available = agents.filter((a) => a.availability === 'available').length;
    const busy = agents.filter((a) => a.availability === 'busy').length;
    const offline = agents.filter((a) => a.availability === 'offline').length;
    return { total, available, busy, offline };
  }, [agents]);

  const onSaved = () => {
    setShowModal(false);
    setEditAgent(null);
    load();
  };

  return (
    <div className="dashboard-page">
      <div className="page-title-row">
        <h2>Agents</h2>
        <button className="primary-btn" type="button" onClick={() => { setEditAgent(null); setShowModal(true); }}>
          + New Agent
        </button>
      </div>

      <section className="dashboard-kpis agents-kpis">
        <article className="dashboard-kpi-card agents-kpi-card agents-kpi-card--total">
          <span>Total Agents</span>
          <strong>{kpis.total}</strong>
        </article>
        <article className="dashboard-kpi-card agents-kpi-card agents-kpi-card--available">
          <span>Available</span>
          <strong>{kpis.available}</strong>
        </article>
        <article className="dashboard-kpi-card agents-kpi-card agents-kpi-card--busy">
          <span>Busy</span>
          <strong>{kpis.busy}</strong>
        </article>
        <article className="dashboard-kpi-card agents-kpi-card agents-kpi-card--offline">
          <span>Offline</span>
          <strong>{kpis.offline}</strong>
        </article>
      </section>

      <div className="filter-bar">
        <input
          className="filter-input"
          placeholder="Search name, email, region..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="filter-select" value={filterAvail} onChange={(e) => setFilterAvail(e.target.value)}>
          <option value="">All Availability</option>
          {AVAILABILITY_OPTIONS.map((o) => (
            <option key={o} value={o}>{AVAIL_LABEL[o]}</option>
          ))}
        </select>
        <select className="filter-select" value={filterRegion} onChange={(e) => setFilterRegion(e.target.value)}>
          <option value="">All Regions</option>
          {regions.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select className="filter-select" value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>

      {loading ? <p>Loading agents...</p> : null}
      {error ? <p className="error-text">{error}</p> : null}

      {!loading && !error ? (
        <div className="dashboard-panel dashboard-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Region</th>
                <th>Capacity</th>
                <th>Active Leads</th>
                <th>Availability</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAgents.length ? (
                filteredAgents.map((agent) => (
                  <tr key={agent.id}>
                    <td>
                      <button type="button" className="lead-name-btn" onClick={() => setDrawerAgentId(agent.id)}>
                        {agent.name}
                      </button>
                    </td>
                    <td>{agent.email}</td>
                    <td>{agent.phone || '—'}</td>
                    <td>{agent.region || '—'}</td>
                    <td>{agent.max_capacity ?? '—'}</td>
                    <td>{agent.active_lead_count ?? '—'}</td>
                    <td>
                      <span className={`availability-pill availability-pill--${agent.availability || 'offline'}`}>
                        {AVAIL_LABEL[agent.availability] || 'Offline'}
                      </span>
                    </td>
                    <td>
                      {agent.is_active ? <span className="badge-green">Active</span> : <span className="badge-slate">Inactive</span>}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          type="button"
                          className="icon-action-btn"
                          title="View agent"
                          aria-label="View agent"
                          data-tooltip="View agent"
                          onClick={() => setDrawerAgentId(agent.id)}
                        >
                          <FiEye />
                        </button>
                        <button
                          type="button"
                          className="icon-action-btn"
                          title="Edit agent"
                          aria-label="Edit agent"
                          data-tooltip="Edit agent"
                          onClick={() => { setEditAgent(agent); setShowModal(true); }}
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          type="button"
                          className="icon-action-btn"
                          title="Capacity"
                          aria-label="Capacity"
                          data-tooltip="Capacity"
                          onClick={() => setCapacityAgent(agent)}
                        >
                          <FiBarChart2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="9">No agents found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {showModal ? (
        <AgentModal
          agent={editAgent}
          onClose={() => { setShowModal(false); setEditAgent(null); }}
          onSaved={onSaved}
        />
      ) : null}

      {capacityAgent ? <CapacityModal agent={capacityAgent} onClose={() => setCapacityAgent(null)} /> : null}

      {drawerAgentId ? (
        <AgentDrawer
          agentId={drawerAgentId}
          onClose={() => setDrawerAgentId(null)}
          onEdit={(agent) => { setDrawerAgentId(null); setEditAgent(agent); setShowModal(true); }}
          onCapacity={(agent) => { setDrawerAgentId(null); setCapacityAgent(agent); }}
        />
      ) : null}
    </div>
  );
}

export default AgentsPage;
